import { useCallback, useEffect, useState, type ReactNode } from 'react'
import HomeRoundedIcon from '@mui/icons-material/HomeRounded'
import DomainRoundedIcon from '@mui/icons-material/DomainRounded'
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded'
import InboxRoundedIcon from '@mui/icons-material/InboxRounded'
import FeedbackRoundedIcon from '@mui/icons-material/FeedbackRounded'
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded'
import SpeedRoundedIcon from '@mui/icons-material/SpeedRounded'
import TerminalRoundedIcon from '@mui/icons-material/TerminalRounded'
import AnalyticsRoundedIcon from '@mui/icons-material/AnalyticsRounded'
import MonetizationOnRoundedIcon from '@mui/icons-material/MonetizationOnRounded'
import SavingsRoundedIcon from '@mui/icons-material/SavingsRounded'
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded'
import FlagRoundedIcon from '@mui/icons-material/FlagRounded'
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded'
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded'
import RuleRoundedIcon from '@mui/icons-material/RuleRounded'
import AutoAwesomeMosaicRoundedIcon from '@mui/icons-material/AutoAwesomeMosaicRounded'
import RateReviewRoundedIcon from '@mui/icons-material/RateReviewRounded'
import TuneRoundedIcon from '@mui/icons-material/TuneRounded'
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded'
import PlayCircleFilledWhiteRoundedIcon from '@mui/icons-material/PlayCircleFilledWhiteRounded'
import HubRoundedIcon from '@mui/icons-material/HubRounded'
import PsychologyRoundedIcon from '@mui/icons-material/PsychologyRounded'
import ScienceRoundedIcon from '@mui/icons-material/ScienceRounded'
import RocketLaunchRoundedIcon from '@mui/icons-material/RocketLaunchRounded'
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded'
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded'
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import { navigateOwner, type OwnerRoute } from '../ownerRouter'
import { useNls } from '../../../nls/nlsProvider'
import { useRoles } from '../context/RolesContext'

const STORAGE_KEY = 'timelining_owner_rail_collapsed'

const getStoredCollapsed = (): boolean => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return false
    return window.localStorage.getItem(STORAGE_KEY) === 'true'
  } catch {
    return false
  }
}

const setStoredCollapsed = (collapsed: boolean): void => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return
    window.localStorage.setItem(STORAGE_KEY, String(collapsed))
  } catch {
    // Ignore private browsing exceptions
  }
}

export type RailItemDef = {
  id: string
  label: string
  icon: ReactNode
  route: OwnerRoute
  isActive: (current: OwnerRoute) => boolean
}

export type RailGroupDef = {
  id: string
  title: string
  items: RailItemDef[]
}

export const OwnerRail = ({ currentRoute }: { currentRoute: OwnerRoute }) => {
  const { t } = useNls()
  const { role } = useRoles()
  const [collapsed, setCollapsed] = useState<boolean>(getStoredCollapsed)

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev
      setStoredCollapsed(next)
      return next
    })
  }, [])

  // Keyboard shortcut: '[' toggles collapsed
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.key === '[' &&
        !['INPUT', 'TEXTAREA', 'SELECT'].includes((e.target as HTMLElement)?.tagName) &&
        !(e.target as HTMLElement)?.isContentEditable
      ) {
        e.preventDefault()
        toggleCollapsed()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [toggleCollapsed])

  const groups: RailGroupDef[] = [
    {
      id: 'home',
      title: t('NLSOwnerConsoleGroupHome'),
      items: [
        {
          id: 'nav-home',
          label: t('NLSOwnerConsoleNavHome'),
          icon: <HomeRoundedIcon fontSize="small" />,
          route: { view: 'home' },
          isActive: (r) => r.view === 'home',
        },
      ],
    },
    {
      id: 'people-workspaces',
      title: t('NLSOwnerConsoleGroupPeopleWorkspaces'),
      items: [
        {
          id: 'nav-workspaces',
          label: t('NLSOwnerConsoleNavWorkspaces'),
          icon: <DomainRoundedIcon fontSize="small" />,
          route: { view: 'workspaces' },
          isActive: (r) => r.view === 'workspaces' || r.view === 'workspace-detail',
        },
        {
          id: 'nav-people',
          label: t('NLSOwnerConsoleNavPeople'),
          icon: <PeopleAltRoundedIcon fontSize="small" />,
          route: { view: 'people' },
          isActive: (r) => r.view === 'people' || r.view === 'person-detail',
        },
      ],
    },
    {
      id: 'support',
      title: t('NLSOwnerConsoleGroupSupport'),
      items: [
        {
          id: 'nav-inbox',
          label: t('NLSOwnerConsoleNavInbox'),
          icon: <InboxRoundedIcon fontSize="small" />,
          route: { view: 'inbox' },
          isActive: (r) => r.view === 'inbox' || r.view === 'ticket-detail',
        },
        {
          id: 'nav-feedback',
          label: t('NLSOwnerConsoleNavFeedback'),
          icon: <FeedbackRoundedIcon fontSize="small" />,
          route: { view: 'feedback' },
          isActive: (r) => r.view === 'feedback' || r.view === 'feedback-detail',
        },
        {
          id: 'nav-broadcast',
          label: t('NLSOwnerConsoleNavBroadcast'),
          icon: <CampaignRoundedIcon fontSize="small" />,
          route: { view: 'broadcast' },
          isActive: (r) => r.view === 'broadcast' || r.view === 'broadcast-new' || r.view === 'broadcast-detail',
        },
      ],
    },
    {
      id: 'health',
      title: t('NLSOwnerConsoleGroupHealth'),
      items: [
        {
          id: 'nav-ops',
          label: t('NLSOwnerConsoleNavOps'),
          icon: <SpeedRoundedIcon fontSize="small" />,
          route: { view: 'ops' },
          isActive: (r) => r.view === 'ops' || r.view === 'incident-detail',
        },
        {
          id: 'nav-logs',
          label: t('NLSOwnerConsoleNavLogs'),
          icon: <TerminalRoundedIcon fontSize="small" />,
          route: { view: 'logs' },
          isActive: (r) => r.view === 'logs' || r.view === 'logs-detail',
        },
        {
          id: 'nav-analytics',
          label: t('NLSOwnerConsoleNavAnalytics'),
          icon: <AnalyticsRoundedIcon fontSize="small" />,
          route: { view: 'analytics' },
          isActive: (r) => r.view === 'analytics',
        },
        {
          id: 'nav-revenue',
          label: t('NLSOwnerConsoleNavRevenue'),
          icon: <MonetizationOnRoundedIcon fontSize="small" />,
          route: { view: 'revenue' },
          isActive: (r) => r.view === 'revenue',
        },
        {
          id: 'nav-costs',
          label: t('NLSOwnerConsoleNavCosts'),
          icon: <SavingsRoundedIcon fontSize="small" />,
          route: { view: 'costs' },
          isActive: (r) => r.view === 'costs',
        },
      ],
    },
    {
      id: 'config',
      title: t('NLSOwnerConsoleGroupConfig'),
      items: [
        {
          id: 'nav-settings',
          label: t('NLSOwnerConsoleNavSettings'),
          icon: <SettingsRoundedIcon fontSize="small" />,
          route: { view: 'config-settings' },
          isActive: (r) => r.view === 'config-settings',
        },
        {
          id: 'nav-flags',
          label: t('NLSOwnerConsoleNavFlags'),
          icon: <FlagRoundedIcon fontSize="small" />,
          route: { view: 'config-flags' },
          isActive: (r) => r.view === 'config-flags' || r.view === 'config-flag-detail',
        },
        {
          id: 'nav-cohorts',
          label: t('NLSOwnerConsoleNavCohorts'),
          icon: <GroupsRoundedIcon fontSize="small" />,
          route: { view: 'config-cohorts' },
          isActive: (r) => r.view === 'config-cohorts' || r.view === 'config-cohort-detail',
        },
        {
          id: 'nav-economy',
          label: t('NLSOwnerConsoleNavEconomy'),
          icon: <AccountBalanceWalletRoundedIcon fontSize="small" />,
          route: { view: 'config-economy' },
          isActive: (r) => r.view === 'config-economy',
        },
        {
          id: 'nav-plan-limits',
          label: t('NLSOwnerConsoleNavPlanLimits'),
          icon: <RuleRoundedIcon fontSize="small" />,
          route: { view: 'config-plan-limits' },
          isActive: (r) => r.view === 'config-plan-limits',
        },
        {
          id: 'nav-templates',
          label: t('NLSOwnerConsoleNavTemplates'),
          icon: <AutoAwesomeMosaicRoundedIcon fontSize="small" />,
          route: { view: 'config-templates' },
          isActive: (r) => r.view === 'config-templates' || r.view === 'config-template-detail',
        },
        {
          id: 'nav-feedback-authoring',
          label: t('NLSOwnerConsoleNavFeedbackAuthoring'),
          icon: <RateReviewRoundedIcon fontSize="small" />,
          route: { view: 'config-feedback-authoring' },
          isActive: (r) => r.view === 'config-feedback-authoring' || r.view === 'config-feedback-authoring-detail',
        },
        {
          id: 'nav-experience-engine',
          label: t('NLSOwnerConsoleNavExperienceEngine'),
          icon: <TuneRoundedIcon fontSize="small" />,
          route: { view: 'config-experience-engine' },
          isActive: (r) => r.view === 'config-experience-engine',
        },
        {
          id: 'nav-moderation',
          label: t('NLSOwnerConsoleNavModeration'),
          icon: <SecurityRoundedIcon fontSize="small" />,
          route: { view: 'config-moderation' },
          isActive: (r) => r.view === 'config-moderation',
        },
        {
          id: 'nav-actions',
          label: t('NLSOwnerConsoleNavActions'),
          icon: <PlayCircleFilledWhiteRoundedIcon fontSize="small" />,
          route: { view: 'config-actions' },
          isActive: (r) => r.view === 'config-actions',
        },
        {
          id: 'nav-integrations',
          label: t('NLSOwnerConsoleNavIntegrations'),
          icon: <HubRoundedIcon fontSize="small" />,
          route: { view: 'config-integrations' },
          isActive: (r) => r.view === 'config-integrations',
        },
        {
          id: 'nav-line',
          label: t('NLSOwnerConsoleLineTab'),
          icon: <PsychologyRoundedIcon fontSize="small" />,
          route: { view: 'config-line' },
          isActive: (r) => r.view === 'config-line',
        },
        {
          id: 'nav-playground',
          label: t('NLSOwnerConsoleNavPlayground'),
          icon: <ScienceRoundedIcon fontSize="small" />,
          route: { view: 'config-playground' },
          isActive: (r) => r.view === 'config-playground',
        },
        {
          id: 'nav-client-outreach',
          label: t('NLSOwnerConsoleNavClientOutreach'),
          icon: <MailOutlineRoundedIcon fontSize="small" />,
          route: { view: 'config-client-outreach' },
          isActive: (r) => r.view === 'config-client-outreach',
        },
        // R35 DL3.b: founder-only (the demo lane callables are founder-only too).
        ...(role === 'founder'
          ? [{
            id: 'nav-demo-lane',
            label: t('NLSOwnerConsoleNavDemoLane'),
            icon: <RocketLaunchRoundedIcon fontSize="small" />,
            route: { view: 'config-demo-lane' } as OwnerRoute,
            isActive: (r: OwnerRoute) => r.view === 'config-demo-lane',
          }]
          : []),
      ],
    },
  ]

  const activityItem: RailItemDef = {
    id: 'nav-activity',
    label: t('NLSOwnerConsoleNavActivity'),
    icon: <HistoryRoundedIcon fontSize="small" />,
    route: { view: 'activity' },
    isActive: (r) => r.view === 'activity' || r.view === 'activity-detail',
  }

  const supportAccessItem: RailItemDef = {
    id: 'nav-support-access',
    label: t('NLSOwnerConsoleNavSupportAccess'),
    icon: <SecurityRoundedIcon fontSize="small" />,
    route: { view: 'support-access' },
    isActive: (r) => r.view === 'support-access',
  }

  return (
    <aside
      className={`ownerRail ${collapsed ? 'ownerRail--collapsed' : ''}`}
      aria-label="Owner console navigation"
    >
      <div className="ownerRailBrand">
        <button
          type="button"
          className="ownerRailBrandMain"
          onClick={() => navigateOwner({ view: 'home' })}
          title="Timelining · Owner OS"
        >
          <span className="ownerRailLogoMark">T</span>
          {!collapsed && (
            <div className="ownerRailBrandText">
              <strong>Timelining</strong>
              <span>{t('NLSOwnerConsoleBrandSub')}</span>
            </div>
          )}
        </button>
        <button
          type="button"
          className="ownerRailCollapseToggle"
          onClick={toggleCollapsed}
          title={collapsed ? `${t('NLSOwnerConsoleExpandRail')} ([)` : `${t('NLSOwnerConsoleCollapseRail')} ([)`}
          aria-label={collapsed ? t('NLSOwnerConsoleExpandRail') : t('NLSOwnerConsoleCollapseRail')}
        >
          {collapsed ? <ChevronRightRoundedIcon fontSize="small" /> : <ChevronLeftRoundedIcon fontSize="small" />}
        </button>
      </div>

      <nav className="ownerRailScrollArea">
        {groups.map((group) => (
          <section className="ownerRailGroup" key={group.id} aria-label={group.title}>
            <div className="ownerRailGroupTitle" aria-hidden={collapsed}>
              {group.title}
            </div>
            {group.items.map((item) => {
              const active = item.isActive(currentRoute)
              return (
                <button
                  key={item.id}
                  type="button"
                  className={`ownerRailItem ${active ? 'ownerRailItem--active' : ''}`}
                  onClick={() => navigateOwner(item.route)}
                  title={collapsed ? item.label : undefined}
                  aria-current={active ? 'page' : undefined}
                >
                  <span className="ownerRailItemIcon">{item.icon}</span>
                  <span className="ownerRailItemLabel">{item.label}</span>
                </button>
              )
            })}
          </section>
        ))}
      </nav>

      {role === 'founder' ? <div className="ownerRailFoot">
        <button
          type="button"
          className={`ownerRailItem ${supportAccessItem.isActive(currentRoute) ? 'ownerRailItem--active' : ''}`}
          onClick={() => navigateOwner(supportAccessItem.route)}
          title={collapsed ? supportAccessItem.label : undefined}
          aria-current={supportAccessItem.isActive(currentRoute) ? 'page' : undefined}
        >
          <span className="ownerRailItemIcon">{supportAccessItem.icon}</span>
          <span className="ownerRailItemLabel">{supportAccessItem.label}</span>
        </button>
        <button
          type="button"
          className={`ownerRailItem ${activityItem.isActive(currentRoute) ? 'ownerRailItem--active' : ''}`}
          onClick={() => navigateOwner(activityItem.route)}
          title={collapsed ? activityItem.label : undefined}
          aria-current={activityItem.isActive(currentRoute) ? 'page' : undefined}
        >
          <span className="ownerRailItemIcon">{activityItem.icon}</span>
          <span className="ownerRailItemLabel">{activityItem.label}</span>
        </button>
      </div> : null}
    </aside>
  )
}
